import * as React from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { format } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { layoutBarRow } from '../../allDayBars'
import { AllDayMore } from './AllDayMore'
import { PositionedBar } from './PositionedBar'
import { GRID_COLUMNS } from './weekGeometry'

type Props = {
  days: Date[]
  /** Every all-day event overlapping the week; multi-day ones become one bar. */
  allDayEvents: CalendarEvent[]
  /** Opens the day view from a crowded day's "+N more". */
  onShowDay?: (day: Date) => void
}

/** Header row — day-of-week labels, date badges, and all-day bars. */
export function WeekHeaderRow({ days, allDayEvents, onShowDay }: Props) {
  const theme = useTheme()
  const { segments, slotsByDay, hiddenByDay } = React.useMemo(
    () => layoutBarRow(allDayEvents, days),
    [allDayEvents, days],
  )
  const laneRows = Math.max(
    0,
    ...slotsByDay.map((slots, i) => slots + (hiddenByDay[i] > 0 ? 1 : 0)),
  )

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        rowGap: '2px',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box aria-hidden sx={{ gridColumn: 1, gridRow: 1 }} />
      {days.map((day, i) => (
        <Box
          key={day.toISOString()}
          data-test-id="week-day-header"
          sx={{
            gridColumn: i + 2,
            gridRow: 1,
            px: 0.5,
            pt: 1,
            pb: laneRows > 0 ? 0 : 1,
            minWidth: 0,
            textAlign: 'center',
            borderLeft: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
          }}
        >
          <Box
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: theme.palette.text.secondary,
            }}
          >
            {format(day, 'EEE')}
          </Box>
          <Box
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: '14px',
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {format(day, 'd')}
          </Box>
        </Box>
      ))}
      {laneRows > 0 &&
        days.map((day, i) => (
          <Box
            key={`all-day-${day.toISOString()}`}
            aria-hidden
            sx={{
              gridColumn: i + 2,
              gridRow: `2 / span ${laneRows + 1}`,
              borderLeft: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.grey[50],
            }}
          />
        ))}
      {segments.map((segment) => (
        <PositionedBar
          key={`${segment.event.id}-${segment.startCol}`}
          segment={segment}
          variant="allDayWeek"
          firstColumn={2}
          firstRow={2}
        />
      ))}
      {days.map(
        (day, i) =>
          hiddenByDay[i] > 0 && (
            <Box
              key={`more-${day.toISOString()}`}
              sx={{
                gridColumn: i + 2,
                gridRow: 2 + slotsByDay[i],
                zIndex: 2,
                px: '4px',
              }}
            >
              <AllDayMore
                count={hiddenByDay[i]}
                day={day}
                onShowDay={onShowDay}
              />
            </Box>
          ),
      )}
    </Box>
  )
}
